import React, { useEffect } from 'react';
import '../themes/cryptoramos-premium.css';

// ============================================
// Type Definitions
// ============================================
export interface Step {
    id: string;
    title: string;
    description: string;
    status: 'completed' | 'active' | 'upcoming';
    link?: string;
    linkText?: string;
}

export interface Resource {
    id: string;
    title: string;
    description: string;
    icon: string;
    tag?: string;
    link: string;
}

export interface FAQItem {
    question: string;
    answer: string;
}

// Admin-editable content types
export interface ProjectData {
    id: string;
    title: string;
    badge: string;
    description: string;
    link: string;
    cta: string;
    launched: string;
    featured: boolean;
}

export interface EventData {
    id: string;
    title: string;
    location: string;
    date: string;
    type: 'past' | 'upcoming';
    role: string;
    promo?: string;
}

export interface TestimonialData {
    id: string;
    name: string;
    handle: string;
    quote: string;
    initials: string;
}

export interface CommunityData {
    id: string;
    name: string;
    platform: string;
    members: string;
}

export interface OnboarderTemplateData {
    identity: {
        name: string;
        handle: string;
        avatar: string;
        welcome: string;
        bio: string;
    };
    hero: {
        welcome: string;
        headline: string;
        subline: string;
        ctaText: string;
        ctaLink: string;
    };
    learningPath: Step[];
    resources: Resource[];
    faq: FAQItem[];
    newsletter?: {
        title: string;
        description: string;
        buttonText?: string;
    };
    social: {
        twitter?: string;
        youtube?: string;
        discord?: string;
        telegram?: string;
        telegramBusiness?: string;
        patreon?: string;
    };
    // Admin-editable content (optional - falls back to defaults)
    projects?: ProjectData[];
    events?: EventData[];
    testimonials?: TestimonialData[];
    communities?: CommunityData[];
}

// ============================================
// Icons (Exact Reference)
// ============================================
const TelegramIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z" />
    </svg>
);

const XIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
);

const YouTubeIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 4-8 4z" />
    </svg>
);

const DiscordIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2763-3.68-.2763-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.419-2.1568 2.419zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.419-2.1568 2.419z" />
    </svg>
);

const PatreonIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <path d="M14.82 2.41c3.96 0 7.18 3.24 7.18 7.21 0 3.96-3.22 7.18-7.18 7.18-3.97 0-7.21-3.22-7.21-7.18 0-3.97 3.24-7.21 7.21-7.21M2 21.6h3.5V2.41H2V21.6z" />
    </svg>
);

// ============================================
// Background Effects (Exact Reference)
// ============================================
const BackgroundEffects: React.FC = () => (
    <>
        <div className="bg-grid"></div>
        <div className="bg-radial"></div>
        <div className="bg-grain"></div>
    </>
);

// ============================================
// Navbar (Exact Reference Structure)
// ============================================
const Navbar: React.FC<{ name: string }> = ({ name }) => (
    <nav className="navbar">
        <div className="container nav-content">
            <div className="logo">
                <img
                    src="https://cryptoramos.com/wp-content/uploads/2026/01/logo.png"
                    alt="CryptoRamos"
                    className="logo-img"
                />
                <span>{name}</span>
            </div>
            <div className="nav-links">
                <a href="#home">Home</a>
                <a href="#community">Community</a>
                <a href="#projects">Projects</a>
            </div>
            <a
                href="/start"
                className="nav-cta btcc-promo"
            >
                Start Here
            </a>
        </div>
    </nav>
);

// ============================================
// Hero (Exact Reference Structure)
// ============================================
const Hero: React.FC<{ data: OnboarderTemplateData }> = ({ data }) => {
    const { social } = data;

    return (
        <header className="hero container" id="home">
            <div className="hero-layout">
                <div className="hero-content">
                    <div className="hero-badge">Oscar Ramos • Trading Education</div>

                    <h1 className="hero-title">
                        LEARN to Trade<br />
                        <span className="hero-highlight">Before it's Too Late</span>
                    </h1>

                    <p className="hero-subtitle">
                        Join Oscar on his trading journey and copy his trades!
                    </p>

                    <a href="/start" className="hero-premium-cta">
                        <span className="cta-content">
                            <span className="cta-title">Start Your Journey</span>
                            <span className="cta-subtitle">The Complete Roadmap</span>
                        </span>
                        <div className="cta-shine"></div>
                        <svg className="cta-arrow" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </a>

                    <div className="social-cta">
                        {social.youtube && (
                            <a href={social.youtube} className="btn btn-primary" target="_blank" rel="noopener noreferrer">
                                <YouTubeIcon /> Subscribe
                            </a>
                        )}
                        {social.twitter && (
                            <a href={social.twitter} className="btn btn-dark" target="_blank" rel="noopener noreferrer">
                                <XIcon /> Follow on X
                            </a>
                        )}
                        {social.telegram && (
                            <a href={social.telegram} className="btn btn-dark" target="_blank" rel="noopener noreferrer">
                                <TelegramIcon /> Join Community
                            </a>
                        )}
                    </div>
                </div>

                <div className="hero-video">
                    <div className="video-caption">
                        <p>Get access to my trades & community for free</p>
                        <span className="video-step-badge">
                            <span className="pulse-dot"></span>
                            Step 1: Watch This
                        </span>
                    </div>
                    <div
                        className="video-wrapper"
                        style={{
                            backgroundImage: 'url(https://img.youtube.com/vi/yBKXj9NC8cU/maxresdefault.jpg)',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center'
                        }}
                    >
                        <iframe
                            src="https://www.youtube.com/embed/yBKXj9NC8cU?rel=0&modestbranding=1"
                            title="Oscar Ramos - Trading"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    </div>
                    <div className="video-glow"></div>
                </div>
            </div>

            <div className="floating-icons">
                <div className="icon-box tg"><TelegramIcon /></div>
                <div className="icon-box yt"><YouTubeIcon /></div>
                <div className="icon-box x"><XIcon /></div>
            </div>
        </header>
    );
};

// ============================================
// About (Exact Reference Structure)
// ============================================
// About component removed in favor of MissionSection

// ============================================
// BTCC Logo Icon
// ============================================
const BTCCIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// ============================================
// Promotional Banner (Premium Styled)
// ============================================
const PromoBanner: React.FC = () => (
    <section className="promo-banner">
        <div className="promo-container container">
            <div className="promo-content">
                <div className="promo-badge">Exclusive Offer</div>
                <h3 className="promo-headline">
                    Get <span className="highlight">$40,000+</span> in rewards, lower fees, 500x leverage & deposit bonuses
                </h3>
                <p className="promo-subtext">
                    I made a sign up and deposit guide on BTCC just for you!
                </p>
            </div>
            <a
                href="https://www.btcc.com/market-promotion/bonus2/kol?name=Oscar#signUp"
                target="_blank"
                rel="noopener noreferrer"
                className="promo-cta"
            >
                <BTCCIcon />
                <span>BTCC</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M7 17L17 7M17 7H7M17 7V17" />
                </svg>
            </a>
        </div>
    </section>
);

// ============================================
// Ecosystem (Exact Reference Structure)
// ============================================
const Ecosystem: React.FC<{ resources: Resource[]; social: OnboarderTemplateData['social'] }> = ({ resources, social }) => {
    useEffect(() => {
        const cards = document.querySelectorAll('.ecosystem-card');
        const handleMouseMove = (e: MouseEvent) => {
            cards.forEach(card => {
                const rect = card.getBoundingClientRect();
                const x = ((e.clientX - rect.left) / rect.width) * 100;
                const y = ((e.clientY - rect.top) / rect.height) * 100;
                (card as HTMLElement).style.setProperty('--mouse-x', `${x}%`);
                (card as HTMLElement).style.setProperty('--mouse-y', `${y}%`);
            });
        };
        window.addEventListener('mousemove', handleMouseMove);
        return () => window.removeEventListener('mousemove', handleMouseMove);
    }, []);

    return (
        <section className="ecosystem-section container" id="community">
            <h2 className="section-title">Our Ecosystem</h2>
            <div className="ecosystem-grid">
                {resources.map((res) => (
                    <div key={res.id} className="ecosystem-card glass-card">
                        <div className="card-avatars">
                            <span className="avatar" style={{ background: '#8A2BE2' }}></span>
                            <span className="avatar" style={{ background: '#00D1FF' }}></span>
                            <span className="avatar" style={{ background: '#FF6B6B' }}></span>
                        </div>
                        <h3>{res.title}</h3>
                        <p>{res.description}</p>
                        <a href={res.link} className="btn-link" target="_blank" rel="noopener noreferrer">
                            Access Now ↗
                        </a>
                    </div>
                ))}

                <div className="ecosystem-card glass-card social-card">
                    <h3>Social Channels</h3>
                    <div className="social-links-vertical">
                        {social.telegram && (
                            <a href={social.telegram} className="social-link-item">
                                <TelegramIcon /> Join Telegram
                            </a>
                        )}
                        {social.telegramBusiness && (
                            <a href={social.telegramBusiness} className="social-link-item">
                                <TelegramIcon /> Telegram (Business)
                            </a>
                        )}
                        {social.twitter && (
                            <a href={social.twitter} className="social-link-item">
                                <XIcon /> Follow on X
                            </a>
                        )}
                        {social.youtube && (
                            <a href={social.youtube} className="social-link-item">
                                <YouTubeIcon /> Subscribe on YouTube
                            </a>
                        )}
                        {social.discord && (
                            <a href={social.discord} className="social-link-item">
                                <DiscordIcon /> Join Discord
                            </a>
                        )}
                        {social.patreon && (
                            <a href={social.patreon} className="social-link-item">
                                <PatreonIcon /> Support on Patreon
                            </a>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

// ============================================
// Feature Icons (Matching Reference)
// ============================================
const GlobeIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <path d="M2 12h20" />
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
);

const ChainIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
);

const ChartIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 3v18h18" />
        <path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3" />
    </svg>
);

const NewsIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2Zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2" />
        <path d="M18 14h-8" />
        <path d="M15 18h-5" />
        <path d="M10 6h8v4h-8V6Z" />
    </svg>
);

// ============================================
// Mission & Features (Unified Section)
// ============================================
const MissionSection: React.FC<{ bio: string }> = ({ bio }) => (
    <section className="mission-section container" id="about">
        <div className="mission-grid">
            <div className="mission-content">
                <div className="mission-badge">About Oscar</div>
                <h2 className="mission-title">
                    Your daily dose of crypto insights, breaking news, and exclusive alpha.
                </h2>
                <p className="mission-bio">{bio}</p>
            </div>

            <div className="mission-features">
                <h3 className="features-title">Clarity in the Volatile Crypto Market</h3>
                <div className="features-list-compact">
                    <div className="feature-item-compact">
                        <GlobeIcon />
                        <span>Global macro analysis</span>
                    </div>
                    <div className="feature-item-compact">
                        <ChainIcon />
                        <span>On-chain metrics & data research</span>
                    </div>
                    <div className="feature-item-compact">
                        <ChartIcon />
                        <span>Actionable trading insights</span>
                    </div>
                    <div className="feature-item-compact">
                        <NewsIcon />
                        <span>Breaking news</span>
                    </div>
                </div>
            </div>
        </div>
    </section>
);

// ============================================
// Projects & Events Section (Premium Styled)
// ============================================

// Default project data (fallback)
const DEFAULT_PROJECTS: ProjectData[] = [
    {
        id: 'slamnet',
        title: 'SlamNet',
        badge: '$SLAM',
        description: 'Web3 information platform aggregating crypto news, project data, and educational resources.',
        link: 'https://slamnet.xyz',
        cta: 'Explore Platform',
        launched: 'July 2024',
        featured: true,
    },
    {
        id: 'slamx',
        title: 'SlamX',
        badge: 'Resources',
        description: 'Community hub with exclusive affiliate deals, trading tools, and educational content.',
        link: 'https://slamx.io',
        cta: 'Access Hub',
        launched: 'May 2024',
        featured: false,
    },
    {
        id: 'inner-circle',
        title: 'Inner Circle',
        badge: 'Premium',
        description: 'Private Discord & Patreon community with portfolio insights, trade alerts, and direct access.',
        link: 'https://patreon.com/OscarRamos',
        cta: 'Join Now',
        launched: '2023',
        featured: false,
    },
] as const;

// EventData interface already defined above

// Default events data (fallback)
const DEFAULT_EVENTS: EventData[] = [
    {
        id: 'bitcoin-2025',
        title: 'Bitcoin 2025',
        location: 'Las Vegas',
        date: 'May 27-29, 2025',
        type: 'past',
        role: 'Affiliate Partner',
    },
    {
        id: 'token2049-singapore',
        title: 'Token2049',
        location: 'Singapore',
        date: 'Sep 18-19, 2024',
        type: 'past',
        role: 'Featured Creator',
    },
    {
        id: 'consensus-2024',
        title: 'Consensus 2024',
        location: 'Austin, TX',
        date: 'May 29-31, 2024',
        type: 'past',
        role: 'The Beat: Live',
    },
    {
        id: 'token2049-dubai',
        title: 'Token2049',
        location: 'Dubai',
        date: 'Apr 18-19, 2024',
        type: 'past',
        role: 'BTCC Fireside Chat',
    },
] as const;

// Default community groups (fallback)
const DEFAULT_COMMUNITIES: CommunityData[] = [
    { id: 'cro', name: '$1 CRO Party', platform: 'Telegram', members: '10K+' },
    { id: 'shib', name: '100B SHIB Party', platform: 'Telegram', members: '15K+' },
];

// Icons for Projects section
const RocketIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
        <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
        <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
        <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
    </svg>
);

const CalendarIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
);

const LocationIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
    </svg>
);

const UsersIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
);

const ProjectsSection: React.FC<{
    projects?: ProjectData[];
    events?: EventData[];
    communities?: CommunityData[];
}> = ({ projects, events, communities }) => {
    // Use provided data or fall back to defaults
    const projectsData = projects ?? [...DEFAULT_PROJECTS];
    const eventsData = events ?? [...DEFAULT_EVENTS];
    const communitiesData = communities ?? [...DEFAULT_COMMUNITIES];

    return (
        <section className="projects-section container" id="projects">
            <h2 className="section-title">Projects & Events</h2>

            {/* Projects Grid */}
            <div className="projects-grid">
                {projectsData.map((project) => (
                    <div key={project.id} className={`project-card glass-card ${project.featured ? 'featured' : ''}`}>
                        <div className="project-header">
                            <div className="project-icon">
                                <RocketIcon />
                            </div>
                            <span className={`project-badge ${project.id === 'slamnet' ? 'token' : project.id === 'inner-circle' ? 'premium' : ''}`}>
                                {project.badge}
                            </span>
                        </div>
                        <h3 className="project-title">{project.title}</h3>
                        <p className="project-description">{project.description}</p>
                        <div className="project-meta">
                            <span className="project-date">Launched {project.launched}</span>
                        </div>
                        <a href={project.link} target="_blank" rel="noopener noreferrer" className="project-cta">
                            {project.cta}
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M7 17L17 7M17 7H7M17 7V17" />
                            </svg>
                        </a>
                    </div>
                ))}
            </div>

            {/* Media Showcase Banner */}
            <div className="media-showcase">
                <h3 className="subsection-title">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polygon points="23 7 16 12 23 17 23 7" />
                        <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                    </svg>
                    Featured Content
                </h3>
                <div className="media-scroll">
                    <a
                        href="https://www.youtube.com/watch?v=yIOYUOIpYAE"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="media-card video"
                    >
                        <img
                            src="https://img.youtube.com/vi/yIOYUOIpYAE/maxresdefault.jpg"
                            alt="XRP Analysis"
                            className="media-thumb"
                        />
                        <div className="media-play">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                <polygon points="5 3 19 12 5 21 5 3" />
                            </svg>
                        </div>
                        <div className="media-label">XRP Analysis</div>
                    </a>
                    <a
                        href="https://www.youtube.com/watch?v=qy92mln7ZBs"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="media-card video"
                    >
                        <img
                            src="https://img.youtube.com/vi/qy92mln7ZBs/maxresdefault.jpg"
                            alt="Market Update"
                            className="media-thumb"
                        />
                        <div className="media-play">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                <polygon points="5 3 19 12 5 21 5 3" />
                            </svg>
                        </div>
                        <div className="media-label">Market Update</div>
                    </a>
                    <a
                        href="https://www.youtube.com/watch?v=-14TboFNldw"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="media-card video"
                    >
                        <img
                            src="https://img.youtube.com/vi/-14TboFNldw/maxresdefault.jpg"
                            alt="The Beat"
                            className="media-thumb"
                        />
                        <div className="media-play">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                <polygon points="5 3 19 12 5 21 5 3" />
                            </svg>
                        </div>
                        <div className="media-label">The Beat</div>
                    </a>
                    <div className="media-card image">
                        <img
                            src="https://i.ibb.co/SXyD7bcy/Screenshot-2026-02-07-173659.png"
                            alt="Live Stream Screenshot"
                            className="media-thumb"
                        />
                        <div className="media-label">Live Coverage</div>
                    </div>
                </div>
            </div>

            {/* Events Timeline */}
            <div className="events-section">
                <h3 className="subsection-title">
                    <CalendarIcon /> Conference Appearances
                </h3>
                <div className="events-timeline">
                    {eventsData.map((event, index) => (
                        <div key={event.id} className={`event-card ${event.type}`}>
                            <div className="event-marker">{index + 1}</div>
                            <div className="event-content">
                                <div className="event-header">
                                    <h4 className="event-title">{event.title}</h4>
                                    {event.type === 'upcoming' && <span className="event-upcoming-badge">Upcoming</span>}
                                </div>
                                <div className="event-details">
                                    <span className="event-location">
                                        <LocationIcon /> {event.location}
                                    </span>
                                    <span className="event-date">{event.date}</span>
                                </div>
                                <div className="event-role">{event.role}</div>
                                {event.promo && (
                                    <div className="event-promo">
                                        Use code <strong>{event.promo}</strong> for discounted tickets
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Community Groups */}
            <div className="communities-section">
                <h3 className="subsection-title">
                    <UsersIcon /> Digital Communities
                </h3>
                <div className="communities-row">
                    {communitiesData.map((community) => (
                        <div key={community.id} className="community-chip">
                            <span className="community-name">{community.name}</span>
                            <span className="community-platform">{community.platform}</span>
                            <span className="community-members">{community.members}</span>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

// ============================================
// Testimonials Section (Team Ramos Comments)
// ============================================
// TestimonialData interface already defined above

// Default testimonials (fallback)
const DEFAULT_TESTIMONIALS: TestimonialData[] = [
    {
        id: '1',
        name: 'Alex (Ghost)',
        handle: '@AlTNodef_X',
        quote: "Ayeee! I'm Alex — also known as Ghost!! Texas born & raised and been grinding crypto since 2024, Mainly Focused on Trading. Proud member of Team Ramos — straight up one of the most loyal, dedicated, put-in-work crews in the whole crypto space. We out here dropping fire content for you guys every single day! If you ever have any questions, don't hesitate to ask. We're all here to grow and become financially free!!",
        initials: 'AG'
    },
    {
        id: '2',
        name: 'JB',
        handle: '@JB3479',
        quote: "JB from the UK. On this crypto ride since 2021. Main Admin for Oscar Ramos YouTube and Telegram since launch . Enjoying the journey to financial freedom. Helping to build and maintain a warm welcoming community for like minded people. Sharing crypto experiences to help enlighten everyone.",
        initials: 'JB'
    },
    {
        id: '3',
        name: 'TJ',
        handle: '@Derek802tj',
        quote: "Investing since 2020, based in Canada, and hooked on Oscar's channel in 2023. Today I'm a proud mod in the hardest-working crypto community online—Team Ramos. We show up daily, cut through the noise, and make sure anyone who thinks crypto is for them gets the support, clarity, and confidence to stay in the game.",
        initials: 'TJ'
    },
    {
        id: '4',
        name: 'Chip-er',
        handle: '@Chip_er_Holland',
        quote: "Welcome to Oscar's channel! Hope everyone finds it as welcoming & informative as I have for nearly 2yrs. I'm Chip-er; born & raised in Texas. Yup...I have the accent. Love the outdoors in any facet. Can't wait to meet more of you. Let's get wealthy together!",
        initials: 'CE'
    }
];

const VerifiedIcon = () => (
    <svg className="verified-badge" viewBox="0 0 24 24" fill="currentColor">
        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
    </svg>
);

const Testimonials: React.FC<{ testimonials?: TestimonialData[] }> = ({ testimonials }) => {
    const items = testimonials ?? DEFAULT_TESTIMONIALS;

    return (
        <section className="testimonials-section">
            <div className="testimonials-container">
                <h2 className="section-title">Comments from <span className="text-gradient">Team Ramos</span></h2>

                <div className="testimonials-viewport">
                    <TestimonialSlider items={items} />
                </div>
            </div>
        </section>
    );
};

// Extracted internal component to use Ref easily
const TestimonialSlider: React.FC<{ items: TestimonialData[] }> = ({ items }) => {
    const scrollRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const scrollContainer = scrollRef.current;
        if (!scrollContainer) return;

        const cardWidth = scrollContainer.firstElementChild?.clientWidth || 300;
        const gap = 24; // 1.5rem
        const step = cardWidth + gap;

        const interval = setInterval(() => {
            if (!scrollContainer) return;
            // Check if we are at end
            if (scrollContainer.scrollLeft + scrollContainer.clientWidth >= scrollContainer.scrollWidth - 10) {
                scrollContainer.scrollTo({ left: 0, behavior: 'smooth' });
            } else {
                scrollContainer.scrollBy({ left: step, behavior: 'smooth' });
            }
        }, 4000);

        return () => clearInterval(interval);
    }, []);

    return (
        <div
            ref={scrollRef}
            className="testimonials-track"
            style={{
                overflowX: 'auto',
                scrollBehavior: 'smooth',
                scrollbarWidth: 'none', // Hide scrollbar
                msOverflowStyle: 'none',
                transform: 'none' // Override the transition transform from CSS if we use scroll
            }}
        >
            {/* We need CSS to not wrap for this to work as a scroll track */}
            {/* Actually, let's keep the existing CSS 'display: flex' but remove the 'transform' on track and use native scroll. 
                 The CSS defined .testimonials-track { display: flex; gap: 1.5rem } which is perfect.
             */}
            {items.map((item) => (
                <div key={item.id} className="testimonial-card" style={{ flexShrink: 0 }}>
                    <div className="testimonial-quote">"{item.quote}"</div>
                    <div className="testimonial-user">
                        <div className="testimonial-avatar">{item.initials}</div>
                        <div className="testimonial-info">
                            <h4>{item.name} <VerifiedIcon /></h4>
                            <span>{item.handle}</span>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    )
}

const Team: React.FC<{ social: OnboarderTemplateData['social'] }> = ({ social }) => (
    <section className="team-section container">
        <div className="team-content">
            <h2 className="section-title">Join 166K+ Army Members</h2>
            <p style={{ color: 'var(--text-secondary)', maxWidth: '600px' }}>
                Get exclusive access to trading signals, market analysis, and a community of like-minded crypto enthusiasts.
            </p>
            <a
                href={social.discord || social.telegram || '#'}
                className="btn btn-primary large"
                target="_blank"
                rel="noopener noreferrer"
            >
                Join the Army
            </a>
        </div>
    </section>
);

// ============================================
// Contact Section (Premium Styled)
// ============================================
const Contact: React.FC<{ identity: OnboarderTemplateData['identity'] }> = ({ identity }) => {
    const [formData, setFormData] = React.useState({ name: '', email: '', message: '' });
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [submitted, setSubmitted] = React.useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const API_BASE = import.meta.env.VITE_API_URL || '';
            const res = await fetch(`${API_BASE}/api/lead`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    type: 'contact',
                    name: formData.name,
                    email: formData.email,
                    message: formData.message,
                    metadata: { source: 'OnboarderTemplate' }
                })
            });

            if (!res.ok) throw new Error('Failed to submit');

            setSubmitted(true);
            setFormData({ name: '', email: '', message: '' });
        } catch (error) {
            console.error('Contact form error:', error);
            alert('Failed to send message. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section className="contact-section container" id="contact">
            <div className="contact-grid">
                <div className="contact-profile">
                    <div className="contact-image-wrapper">
                        <img
                            src="https://media.licdn.com/dms/image/v2/C4E03AQFtjPYZPabzDQ/profile-displayphoto-shrink_800_800/profile-displayphoto-shrink_800_800/0/1655324121199?e=1772064000&v=beta&t=QrBsSCoKJvQ3G-TWLc6VohTZSCbwpxSFNO-eDLfX5Zc"
                            alt={identity.name}
                            className="contact-image"
                        />
                    </div>
                    <h3 className="contact-name">{identity.name}</h3>
                    <p className="contact-role">Crypto Educator & Analyst</p>
                    <p className="contact-bio">
                        Sup fam, Oscar here. I know crypto is the future, I’m ALL IN. I’ve been doing Crypto since 2021. Reaching thousands of people every single day with over 40 Million views and 4,500+ videos on YouTube. This industry changed my life in 2025, so now let me help you become a more confident investor. Together, we will make our crypto bags great!
                    </p>
                </div>

                <div className="contact-form-card">
                    <h2 className="contact-title">Get In Touch</h2>
                    <p className="contact-subtitle">
                        Have a question, partnership inquiry, or just want to connect? Send me a message.
                    </p>

                    {submitted ? (
                        <div className="contact-success">
                            <span className="success-icon">✓</span>
                            <p>Thanks for reaching out! I'll get back to you soon.</p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="contact-form">
                            <div className="form-group">
                                <input
                                    type="text"
                                    placeholder="Your Name"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    required
                                    className="form-input"
                                />
                            </div>
                            <div className="form-group">
                                <input
                                    type="email"
                                    placeholder="Your Email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    required
                                    className="form-input"
                                />
                            </div>
                            <div className="form-group">
                                <textarea
                                    placeholder="Your Message"
                                    value={formData.message}
                                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                    required
                                    rows={4}
                                    className="form-input form-textarea"
                                />
                            </div>
                            <button
                                type="submit"
                                className="btn btn-primary contact-submit"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Sending...' : 'Send Message'}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </section>
    );
};

// ============================================
// Footer (Exact Reference Structure)
// ============================================
const Footer: React.FC<{ name: string }> = ({ name }) => (
    <footer className="footer">
        <div className="footer-watermark">{name}</div>
        <div className="container footer-content">
            <div className="footer-logo">{name}</div>
            <div className="footer-links">
                <a href="#privacy">Privacy Policy</a>
                <a href="#terms">Terms of Service</a>
                <a href="#compliance">Compliance</a>
            </div>
            <div className="copyright">© {new Date().getFullYear()} {name}. All rights reserved.</div>
        </div>
    </footer>
);

// ============================================
// Main Template (Matching Reference App.tsx)
// ============================================
export const OnboarderTemplate: React.FC<{ data: OnboarderTemplateData }> = ({ data }) => {
    const { identity, resources, social } = data;

    return (
        <div className="app">
            <BackgroundEffects />
            <Navbar name={identity.name} />
            <main>
                <Hero data={data} />
                <PromoBanner />
                <MissionSection bio={identity.bio} />
                <Ecosystem resources={resources} social={social} />
                <ProjectsSection
                    projects={data.projects}
                    events={data.events}
                    communities={data.communities}
                />
                <Testimonials testimonials={data.testimonials} />
                <Team social={social} />
                <Contact identity={identity} />
            </main>
            <Footer name={identity.name} />
        </div>
    );
};

export default OnboarderTemplate;
